import { throttle } from '@scripts/misc/utils';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class ScrollThroughBlock {
  constructor() {
    this.scrollSectionContainer = document.querySelector('.js-scroll-through-block');
    this.scrollContainer = this.scrollSectionContainer?.querySelector('.js-scroll-through-images-container');

    this.textBlocks = this.scrollSectionContainer?.querySelectorAll('.js-scroll-through-text-block');
    this.images = this.scrollSectionContainer?.querySelectorAll('.js-scroll-through-image');

    this.timelines = [];

    // if there are no images, return
    if (!this.images?.length) {
      return;
    }

    this.init();
  }

  isScrolledToBottom() {
    return window.innerHeight + window.scrollY + 10 >= document.body.offsetHeight; // Little offset to make sure it works
  }

  init() {
    gsap.registerPlugin(ScrollTrigger);

    if (!this.images?.length) {
      return;
    }

    const checkScrollToBottom = () => {
      // Dirty fix for the SC animation when scrolled to bottom, as it doesn't work properly and "stutter"
      if (this.isScrolledToBottom()) {
        // find the index of the widest image and always toggle its text when scrolled to end
        const widestImageIndex = this.findWidestImageIndex();
        throttle(this.toggleTextBlock(undefined, widestImageIndex), 20);

        this.timelines.forEach((timeline) => {
          // Disable scrolltrigger for all timelines
          timeline.scrollTrigger.disable(false, true);
        });
      } else {
        this.timelines.forEach((timeline) => {
          if (!timeline.scrollTrigger.enabled) {
            // Enable scrolltrigger for all timelines
            timeline.scrollTrigger.enable(false);
          }
        });
      }
    };

    // Listen to scroll event and handle animation disable/enable but only when the scroll-through-block is the last block on the page
    if (this.scrollSectionContainer.nextElementSibling === null) {
      window.addEventListener('scroll', throttle(checkScrollToBottom, 20));
    }

    this.images.forEach((image, index) => {
      // create a timeline for the current image
      const timeline = gsap.timeline({
        scrollTrigger: {
          // give the current image's trigger a unique ID
          id: `scroll-through-image-${index}`,
          trigger: image,
          start: 'top 80%',
          end: 'bottom -30%',
          scrub: 0.3,
          // markers: true, // debug only
          onUpdate: (self) => this.updateTimeline(self, index),
        },
        defaults: { duration: 1, ease: 'none' },
      });

      // add animations to the timeline
      timeline
        .from(image, { opacity: 0.2, width: '60%' })
        .to(image, { opacity: 1, width: '100%' })
        .to(image, { opacity: 1, width: '100%' })
        .to(image, { opacity: 0.4, width: '80%' })
        .to(image, { opacity: 0.2, width: '60%' });

      this.timelines.push(timeline);
    });
  }

  // find the index of the widest image
  findWidestImageIndex() {
    let widestImageIndex = 0;
    let widestImage = this.images[widestImageIndex];

    // loop through all images
    for (let i = 1; i < this.images.length; i++) {
      // if the current image is wider than the widest image, set the current image as the widest image
      if (this.images[i].offsetWidth > widestImage.offsetWidth) {
        widestImageIndex = i;
        widestImage = this.images[widestImageIndex];
      }
    }

    // return the index of the widest image
    return widestImageIndex;
  }

  // toggle the text block for the specified image
  toggleTextBlock(st, index) {
    this.textBlocks.forEach((textBlock) => {
      if (textBlock.classList.contains('visible')) {
        // Hide visible text block
        textBlock.classList.remove('visible');
        textBlock.classList.add('hidden');
      }
    });

    // Show text block by its index
    this.textBlocks[index].classList.remove('hidden');
    this.textBlocks[index].classList.add('visible');
  }

  updateTimeline(st) {
    if (this.isScrolledToBottom()) {
      return;
    }

    // find the index of the widest image
    const widestImageIndex = this.findWidestImageIndex();

    throttle(this.toggleTextBlock(st, widestImageIndex), 20);
  }
}
